import React, { useEffect, useRef, useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { useFilter } from '../../context/FilterContext';
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import { isVideoSrc, resolveMediaUrl } from '../admin/MediaUpload';

export default function HeroBanner() {
  const { data } = useSite();
  const { setCategory, setQuery } = useFilter();
  const slides = data.heroSlides || [];
  const [i, setI] = useState(0);
  // Browsers block autoplay-with-sound until the user has interacted with the
  // page. We start muted (so autoplay succeeds), then unmute on the first user
  // gesture anywhere on the page. The user can also toggle manually.
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [autoUnmuted, setAutoUnmuted] = useState(false);

  useEffect(() => {
    if (autoUnmuted) return;
    const onFirstGesture = () => {
      setMuted(false);
      setAutoUnmuted(true);
      if (videoRef.current) { videoRef.current.muted = false; videoRef.current.play?.().catch(() => {}); }
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
      window.removeEventListener('touchstart', onFirstGesture);
    };
    window.addEventListener('pointerdown', onFirstGesture, { once: false });
    window.addEventListener('keydown', onFirstGesture, { once: false });
    window.addEventListener('touchstart', onFirstGesture, { once: false });
    return () => {
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
      window.removeEventListener('touchstart', onFirstGesture);
    };
  }, [autoUnmuted]);

  useEffect(() => {
    if (slides.length < 2) return;
    if (isVideoSrc(slides[i]?.image)) return; // video advances on its own onEnded
    const t = setInterval(() => setI((v) => (v + 1) % slides.length), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [slides.length, i]);

  if (data.heroEnabled === false) return null;
  if (!slides.length) return null;
  const slide = slides[i];
  const slideIsVideo = isVideoSrc(slide.image);
  const mediaUrl = resolveMediaUrl(slide.image);

  const handleCta = () => {
    // Derive a filter keyword from the CTA label (e.g. "Explore B2C" → "b2c")
    const text = ((slide.cta || '') + ' ' + (slide.ctaLink || '') + ' ' + (slide.id || '')).toLowerCase();
    let cat = null;
    for (const k of ['b2c','b2b','whitelabel','white-label','line-access','lineaccess','platform','join','business']) {
      if (text.includes(k)) { cat = k.replace(/[^a-z0-9]/g, ''); break; }
    }
    if (cat) { setCategory(cat); setQuery(''); }
    // Smoothly scroll the user down to where the storefront grids live
    const target = slide.ctaLink && slide.ctaLink.startsWith('#') ? document.querySelector(slide.ctaLink) : null;
    const fallback = document.querySelector('[data-testid="gamecards-grid"], [data-testid="products-empty"]');
    (target || fallback)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
      {slideIsVideo ? (
        <video
          ref={videoRef}
          key={mediaUrl || slide.id}
          src={mediaUrl}
          autoPlay
          muted={muted}
          playsInline
          onEnded={() => setI((v) => (v + 1) % slides.length)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div
          key={mediaUrl || slide.id}
          className="absolute inset-0 transition-all duration-700"
          style={{
            background: slide.image
              ? `url(${mediaUrl}) center/cover no-repeat`
              : slide.bg
          }}
        />
      )}
      {/* dim overlay when there's media so text is readable */}
      {(slide.image) && (
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
      )}

      <div className="absolute inset-0 opacity-70 pointer-events-none">
        <div className="absolute top-6 left-8 w-24 h-1 bg-[color:var(--gold)]/40 rotate-12 rounded-full blur-[1px]" />
        <div className="absolute top-14 left-20 w-16 h-0.5 bg-[color:var(--gold)]/30 rotate-12 rounded-full blur-[1px]" />
      </div>

      <div className="relative h-full flex items-center px-3 sm:px-6 md:px-10">
        {!slide.hideText && (
          <div className="max-w-[85%] sm:max-w-[70%] md:max-w-[60%]">
            <div className="font-display text-[8px] sm:text-[10px] md:text-xs text-[color:var(--gold)] tracking-[0.25em] sm:tracking-[0.3em] mb-1 truncate">
              {data.branding.logoText} <span className="text-zinc-400">{data.branding.logoAccent}</span>
            </div>
            <h2 className="font-impact text-xl sm:text-2xl md:text-4xl lg:text-5xl text-shimmer leading-[1.05] break-words">
              {slide.title}
            </h2>
            {slide.subtitle && (
              <h3 className="mt-1.5 sm:mt-2 font-impact text-base sm:text-lg md:text-2xl lg:text-3xl text-white leading-tight break-words line-clamp-2">
                {slide.subtitle}
              </h3>
            )}
            {slide.cta && (
              <button onClick={handleCta} className="mt-3 sm:mt-4 md:mt-5 px-4 sm:px-5 py-1.5 sm:py-2 rounded-full bg-[color:var(--gold)] text-black font-bold text-[11px] sm:text-xs md:text-sm hover:scale-105 hover:brightness-110 transition-all click-ripple" data-testid={`hero-cta-${i}`}>
                {slide.cta}
              </button>
            )}
          </div>
        )}
        {/* CTA shown even when slide.hideText is true */}
        {slide.hideText && slide.cta && (
          <button onClick={handleCta} className="absolute left-3 sm:left-6 md:left-10 bottom-10 sm:bottom-12 px-4 sm:px-5 py-1.5 sm:py-2 rounded-full bg-[color:var(--gold)] text-black font-bold text-[11px] sm:text-xs md:text-sm hover:scale-105 hover:brightness-110 transition-all click-ripple" data-testid={`hero-cta-${i}`}>
            {slide.cta}
          </button>
        )}
      </div>

      {slideIsVideo && (
        <button
          type="button"
          onClick={() => {
            const next = !muted;
            setMuted(next);
            setAutoUnmuted(true);
            if (videoRef.current) { videoRef.current.muted = next; if (!next) videoRef.current.play?.().catch(() => {}); }
          }}
          className="absolute right-3 top-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white border border-white/15 flex items-center justify-center backdrop-blur"
          data-testid="hero-mute-toggle"
          aria-label={muted ? 'Unmute hero video' : 'Mute hero video'}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      )}

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {data.heroSliderControls && slides.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-6 bg-[color:var(--gold)]' : 'w-1.5 bg-white/40'}`} />
        ))}
      </div>

      {data.heroSliderControls && slides.length > 1 && (
        <>
          <button onClick={() => setI((v) => (v - 1 + slides.length) % slides.length)} className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/70 text-white items-center justify-center transition"><ChevronLeft size={20} /></button>
          <button onClick={() => setI((v) => (v + 1) % slides.length)} className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/70 text-white items-center justify-center transition"><ChevronRight size={20} /></button>
        </>
      )}
    </div>
  );
}
